import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";

import TransactionTable from "../common/TransactionTalbe/TransactionTable";
import CompaniesSkeleton from "./CompaniesSkeleton";
import SetHomePageButton from "../common/SetHomePageButton";

import { getAllCompanies } from "../../actions/companiesAction";
import { getLastTransaction } from "../../actions/reportsAction";
import "./home.css";
import ProvidersCards from "../../core/containers/ProvidersCards/ProvidersCards";
import { parseBool } from "../../shared/utils/utils.js";
import { useMount } from "shared/hooks/ppHooks";
import { useLocation } from "react-router-dom";
import PinEntryModal from "components/common/PinEntry";
import { cleanStorage } from "shared/utils/cleanStorage";

const Home = ({
  user,
  getAllCompanies,
  allCompanies,
  loading,
  getLastTransaction,
}) => {
  const [columnStyle, setColumnStyle] = useState(
    "col-lg-2 col-md-4 col-sm-6 card-sm"
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hideIsraelCompany, setHideIsraelCompany] = useState(
    localStorage.getItem("hide companies")
  );
  
  const location = useLocation();
  const { random } = location.state || {};
  const showIsraeliCarriers = hideIsraelCompany === "true";
  
  // Use refs for interval management
  const pollIntervalRef = useRef(null);
  const pollingCounterRef = useRef(0);

  // Memoize the refreshColumnStyle function
  const refreshColumnStyle = useCallback(() => {
    const sizeMap = {
      column4: "col-lg-3 col-md-4 col-sm-6 card-md",
      column3: "col-lg-4 col-md-6 col-sm-6 card-lg",
      default: "col-lg-2 col-md-4 col-sm-6 card-sm",
      column6: "col-lg-2 col-md-4 col-sm-6 card-sm",
    };
    
    const size = localStorage.size || "default";
    setColumnStyle(sizeMap[size] || sizeMap.column4);
  }, []);

  // Memoize scrollbar check
  const scrollbarVisible = useCallback((element) => {
    return element.scrollHeight > element.clientHeight;
  }, []);

  // Handle Israeli companies toggle
  const handleIsraeliToggle = useCallback(async (show) => {
    if (show) {
      setHideIsraelCompany("false");
      localStorage.setItem("hide companies", "false");
    } else {
      setHideIsraelCompany("true");
      localStorage.setItem("hide companies", "true");
    }
    getLastTransaction("0", 5);
  }, [getLastTransaction]);

  // Optimized transaction polling with proper cleanup
  const startTransactionPolling = useCallback(async () => {
    pollingCounterRef.current = 0;
    
    const poll = async () => {
      try {
        const transactions = await getLastTransaction("0", 5);
        
        if (!transactions) return;
        
        pollingCounterRef.current++;
        const isPending = transactions.some(
          (t) =>
            t.status.toLowerCase() === "proccessing" ||
            t.status.toLowerCase() === "pending"
        );

        // Progressive polling: 15s, 30s, then stop
        if (isPending && pollingCounterRef.current === 1) {
          pollIntervalRef.current = setTimeout(poll, 15000);
        } else if (isPending && pollingCounterRef.current === 2) {
          pollIntervalRef.current = setTimeout(poll, 30000);
        }
      } catch (error) {
        console.error("Transaction polling error:", error);
      }
    };

    // Start first poll after 10 seconds
    pollIntervalRef.current = setTimeout(poll, 10000);
  }, [getLastTransaction]);

  // Listen for localStorage changes from other tabs/components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "hide companies" && e.newValue !== null) {
        console.log('📡 Storage change detected - hide companies:', e.newValue);
        setHideIsraelCompany(e.newValue);
      }
    };

    // Listen for storage events (cross-tab communication)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event (same-tab communication)
    const handleCustomStorageChange = (e) => {
      if (e.detail && e.detail.key === "hide companies") {
        console.log('📡 Custom storage event - hide companies:', e.detail.value);
        setHideIsraelCompany(e.detail.value);
      }
    };
    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  // Handle keyboard shortcuts
  const handleKeyboardShortcuts = useCallback((e) => {
    console.log('🎹 Key pressed:', e.code, 'KeyCode:', e.keyCode, 'Ctrl:', e.ctrlKey);
    
    const element = document.body;
    
    // Handle scrollbar padding
    if (e.code === "") {
      if (!scrollbarVisible(element)) {
        document.body.classList.add("padding-class");
      } else {
        document.body.classList.remove("padding-class");
      }
    }

    // Handle F9 or Ctrl+I for Israeli companies toggle
    if ((e.ctrlKey && e.keyCode === 73) || e.code === "F9") {
      console.log('🔑 F9 or Ctrl+I detected!');
      e.preventDefault(); // Prevent default browser behavior
      
      const currentSetting = localStorage.getItem("hide companies");
      console.log('Current hide companies setting:', currentSetting);
      
      if (currentSetting === "false") {
        console.log('Hiding Israeli companies...');
        e.returnValue = false;
        handleIsraeliToggle(false);
        // Dispatch custom event for same-tab communication
        window.dispatchEvent(new CustomEvent('localStorageChange', {
          detail: { key: 'hide companies', value: 'true' }
        }));
      } else if (currentSetting === "true") {
        console.log('Showing PIN dialog to unhide Israeli companies...');
        setIsDialogOpen(true);
      }
    }
  }, [scrollbarVisible, handleIsraeliToggle]);

  // Handle showing Israeli companies from router state
  useEffect(() => {
    if (!random) return;

    const showIsraeliCompanies = () => {
      const currentSetting = localStorage.getItem("hide companies");
      
      if (currentSetting === "false") {
        setHideIsraelCompany("true");
        localStorage.setItem("hide companies", "true");
        getLastTransaction("0", 5);
      } else if (currentSetting === "true") {
        setIsDialogOpen(true);
      }
    };

    showIsraeliCompanies();
  }, [random, getLastTransaction]);

  // Initial setup
  useEffect(() => {
    document.title = "Home | Phone Play";
    refreshColumnStyle();
    cleanStorage();
  }, [refreshColumnStyle]);

  // Load companies on mount
  useMount(() => {
    if (!allCompanies || allCompanies.length === 0) {
      getAllCompanies();
    }
  });

  // Setup keyboard shortcuts and transaction polling
  useEffect(() => {
    // Add keyboard event listener
    const keydownHandler = (e) => handleKeyboardShortcuts(e);
    
    // Add with slight delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      window.addEventListener("keydown", keydownHandler);
    }, 100);

    // Start transaction polling
    startTransactionPolling();

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("keydown", keydownHandler);
      
      // Clear any pending polling timeouts
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [handleKeyboardShortcuts, startTransactionPolling]);

  // Memoize skeleton arrays to prevent recreation on every render
  const skeletonsLarge = useMemo(() => [...Array(9)], []);
  const skeletonsSmall = useMemo(() => [...Array(2)], []);

  return (
    <>
      <div>
        <div className="card card-home" style={{ position: "relative" }}>
          {/* Future: Could add dynamic banner image here */}
          <div style={{ position: "absolute", top: "10px", right: "10px", zIndex: 10 }}>
            <SetHomePageButton 
              currentPath="/"
              pageName="Home"
              variant="icon"
            />
          </div>
        </div>
        <div className="">
          <div className="row">
            {/* Loading skeletons - Palestinian carriers */}
            {loading &&
              allCompanies.length === 0 &&
              !showIsraeliCarriers &&
              skeletonsLarge.map((_, i) => (
                <CompaniesSkeleton
                  height={185}
                  columnStyle={columnStyle}
                  key={i}
                />
              ))}
            
            {/* Loading skeletons - Israeli carriers */}
            {loading &&
              allCompanies.length === 0 &&
              showIsraeliCarriers &&
              skeletonsSmall.map((_, i) => (
                <CompaniesSkeleton
                  height={185}
                  columnStyle={columnStyle}
                  key={i}
                />
              ))}

            <ProvidersCards
              key={`providers-${hideIsraelCompany}`}
              columnStyle={columnStyle}
              md={3}
              hideInformationIcon={false}
              hideIsraeliCarriers={parseBool(hideIsraelCompany)}
              xs={4}
            />

            <TransactionTable />
          </div>
        </div>
        <PinEntryModal
          open={isDialogOpen}
          handleClose={() => setIsDialogOpen(false)}
          setHideIsraelCompany={setHideIsraelCompany}
          getLastTransaction={getLastTransaction}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({
  auth: { user },
  companies: { allCompanies, loading },
}) => ({
  user,
  allCompanies,
  loading,
});

export default connect(mapStateToProps, {
  getAllCompanies,
  getLastTransaction,
})(Home);

